import { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(ChartDataLabels);

type PieChartProps = {
    labels: string[];
    values: number[];
    colors?: string[];
    className?: string;
    onReady?: (api: { 
      update: (labels: string[], values: number[], colors?: string[]) => void 
    }) => void;
};

const DEFAULT_COLORS = ['#5B8FF9','#F6BD16','#5AD8A6','#5D7092','#6F5EF9','#F6903D'];

function getOrCreateTooltip(): HTMLDivElement {
  let el = document.getElementById('chartjs-ext-tooltip') as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = 'chartjs-ext-tooltip';
    el.style.position = 'fixed';
    el.style.background = 'rgba(0,0,0,.85)';
    el.style.color = '#fff';
    el.style.borderRadius = '6px';
    el.style.padding = '8px 10px';
    el.style.pointerEvents = 'none';
    el.style.zIndex = '9999';
    el.style.transform = 'translate(-50%, -120%)';
    el.style.whiteSpace = 'nowrap';
    document.body.appendChild(el);
  }
  return el;
}

const externalTooltip = (ctx: any) => {
  const { chart, tooltip } = ctx;
  const el = getOrCreateTooltip();

  if (tooltip.opacity === 0) {
    el.style.opacity = '0';
    return;
  }

  if (tooltip.body) {
    const title = tooltip.title?.[0] ?? '';
    const body = tooltip.body[0]?.lines?.[0] ?? '';
    el.innerHTML = `<div style="font-weight:600;margin-bottom:4px">${title}</div>
                    <div>${body}</div>`;
  }

  const { x, y } = tooltip.caretX !== undefined
    ? { x: tooltip.caretX, y: tooltip.caretY }
    : { x: chart.canvas.offsetLeft, y: chart.canvas.offsetTop };

  const rect = chart.canvas.getBoundingClientRect();
  el.style.left = `${rect.left + tooltip.caretX}px`;
  el.style.top  = `${rect.top + tooltip.caretY}px`;
  el.style.opacity = '1';
};

export default function PieChart({
  labels, values, colors = DEFAULT_COLORS, className, onReady
}: PieChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<any>();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!canvasRef.current) return;

    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = labels && labels.length > 0 ? new Chart(canvasRef.current, {
      type: 'pie',
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderColor: '#fff',
          borderWidth: 2,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: 'end',                         
            align: 'end',                          
            offset: -30,                             
            color: '#fff',
            font: { weight: '600', size: 11 } as any,
            formatter: (v: number) => `${v?.toFixed(0)}%`,
          },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.label}: ${ctx.parsed}%`,
            },
            enabled: false,
            external: externalTooltip,
          }
        }
      },
    }): null;

    // expose api update
    onReady?.({
      update: (newLabels, newValues, newCols) => {
        if (!mountedRef.current || !chartRef.current) return;
        if (!chartRef.current.canvas || !chartRef.current.canvas.ownerDocument) return;
        if (newLabels && newLabels.length > 0) {
          chartRef.current.data.labels = newLabels;
          chartRef.current.data.datasets[0].data = newValues as any;
          chartRef.current.data.datasets[0].backgroundColor = newCols;
          chartRef.current.update();
        }
      }
    });

    return () => {
      mountedRef.current = false;
      chartRef.current?.destroy();
      chartRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div className={className}>
      <canvas ref={canvasRef}/>
    </div>
  );
}
